import React, { useState } from 'react';
import { Plus, Trash2, Copy, Compass, Sparkles, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { PageSection } from '../../../../types';

interface YachtRoutesEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const YachtRoutesEditor: React.FC<YachtRoutesEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};
  const routes: any[] = content.routes || content.items || [
    {
      id: 'r1',
      name: 'Göcek 12 Adalar & Fethiye Körfezi',
      durationDays: 7,
      departurePort: 'Göcek',
      arrivalPort: 'Göcek',
      highlightBays: ['Bedri Rahmi', 'Kleopatra', 'Tersane'],
      coverImage: 'https://images.unsplash.com/photo-1510312305653-8ed496efae75?q=80&w=800&auto=format&fit=crop',
    },
    {
      id: 'r2',
      name: 'Bodrum - Gökova Körfezi Klasik',
      durationDays: 7,
      departurePort: 'Bodrum',
      arrivalPort: 'Bodrum',
      highlightBays: ['Çökertme', 'Sedir Adası', 'İngiliz Limanı'],
      coverImage: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=800&auto=format&fit=crop',
    },
  ];

  const [activeRouteId, setActiveRouteId] = useState<string | null>(routes[0]?.id || null);

  const handleUpdateRoute = (index: number, updates: any) => {
    const next = [...routes];
    next[index] = { ...next[index], ...updates };
    onUpdateContent({ routes: next, items: next });
  };

  const handleAddRoute = () => {
    const newRoute = {
      id: `route_${Date.now().toString(36)}`,
      name: `Yeni Mavi Tur Rotası ${routes.length + 1}`,
      durationDays: 7,
      departurePort: 'Göcek',
      arrivalPort: 'Fethiye',
      highlightBays: ['Sarsala', 'Hamam Koyu'],
      coverImage: 'https://images.unsplash.com/photo-1510312305653-8ed496efae75?q=80&w=800&auto=format&fit=crop',
    };
    const next = [...routes, newRoute];
    onUpdateContent({ routes: next, items: next });
    setActiveRouteId(newRoute.id);
  };

  const handleDuplicateRoute = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const source = routes[index];
    const duplicated = {
      ...JSON.parse(JSON.stringify(source)),
      id: `route_${Date.now().toString(36)}`,
      name: `${source.name} (Kopya)`,
    };
    const next = [...routes];
    next.splice(index + 1, 0, duplicated);
    onUpdateContent({ routes: next, items: next });
    setActiveRouteId(duplicated.id);
  };

  const handleDeleteRoute = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = routes.filter((_, i) => i !== index);
    onUpdateContent({ routes: next, items: next });
    if (activeRouteId === routes[index]?.id) {
      setActiveRouteId(next[0]?.id || null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Headings */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
          <h4 className="text-xs font-bold text-slate-200">Mavi Tur Rotaları Başlıkları</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge || 'POPÜLER SEYİR ROTALARI'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            className="w-full px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-xs text-slate-200 focus:border-cyan-500 outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title || ''}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            placeholder="Ege ve Akdeniz Rotaları..."
            className="w-full px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-xs text-slate-200 focus:border-cyan-500 outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Açıklama Metni</label>
          <textarea
            rows={2}
            value={content.description || ''}
            onChange={(e) => onUpdateContent({ description: e.target.value })}
            className="w-full px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-xs text-slate-200 focus:border-cyan-500 outline-none resize-none"
          />
        </div>
      </div>

      {/* Routes List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Compass className="w-4 h-4 text-cyan-400" />
            <span className="text-xs font-bold text-slate-200">Rotalar ({routes.length})</span>
          </div>
          <button
            type="button"
            onClick={handleAddRoute}
            className="px-2.5 py-1 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-[11px] font-bold flex items-center gap-1 transition"
          >
            <Plus className="w-3 h-3" />
            Rota Ekle
          </button>
        </div>

        <div className="space-y-2">
          {routes.map((route, idx) => {
            const isExpanded = activeRouteId === route.id;
            return (
              <div
                key={route.id || idx}
                className="rounded-xl border border-slate-700/70 bg-slate-900/80 overflow-hidden text-xs"
              >
                {/* Item header */}
                <div
                  onClick={() => setActiveRouteId(isExpanded ? null : route.id)}
                  className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition select-none"
                >
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    <span className="w-5 h-5 rounded-md bg-cyan-500/20 text-cyan-400 font-bold text-[10px] flex items-center justify-center shrink-0">
                      {idx + 1}
                    </span>
                    <span className="font-bold text-slate-200 truncate">
                      {route.name || `Rota #${idx + 1}`}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0 font-medium">
                      ({route.durationDays || 7} Gün)
                    </span>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => handleDuplicateRoute(idx, e)}
                      title="Kopyala"
                      className="p-1 rounded text-slate-400 hover:text-cyan-300 transition"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteRoute(idx, e)}
                      title="Sil"
                      className="p-1 rounded text-slate-400 hover:text-rose-400 transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-slate-400 ml-1" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400 ml-1" />
                    )}
                  </div>
                </div>

                {/* Expanded Fields */}
                {isExpanded && (
                  <div className="p-3 border-t border-slate-800 space-y-3 bg-slate-950/40">
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase">Rota Adı</label>
                      <input
                        type="text"
                        value={route.name || ''}
                        onChange={(e) => handleUpdateRoute(idx, { name: e.target.value })}
                        className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Süre (Gün)</label>
                        <input
                          type="number"
                          min={1}
                          max={30}
                          value={route.durationDays || 7}
                          onChange={(e) => handleUpdateRoute(idx, { durationDays: Number(e.target.value) })}
                          className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Kalkış Limanı</label>
                        <input
                          type="text"
                          value={route.departurePort || ''}
                          onChange={(e) => handleUpdateRoute(idx, { departurePort: e.target.value })}
                          className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase">Varış Limanı</label>
                      <input
                        type="text"
                        value={route.arrivalPort || ''}
                        onChange={(e) => handleUpdateRoute(idx, { arrivalPort: e.target.value })}
                        className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase">Fotoğraf URL'si</label>
                      <input
                        type="url"
                        value={route.coverImage || ''}
                        onChange={(e) => handleUpdateRoute(idx, { coverImage: e.target.value })}
                        className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
